import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { Heart } from 'lucide-react';
import { motion } from 'framer-motion';

const translations = {
  ro: {
    title: 'Produsele Tale Favorite',
    empty: 'Nu ai niciun produs favorit inca.',
    browseProducts: 'Descopera Produsele',
    removeAll: 'Sterge Toate',
  },
  en: {
    title: 'Your Favorite Products',
    empty: "You don't have any favorite products yet.",
    browseProducts: 'Browse Products',
    removeAll: 'Remove All',
  }
};

export const FavoritesPage = () => {
  const { API, language } = useApp();
  const [favorites, setFavorites] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const t = translations[language] || translations.ro;

  useEffect(() => {
    const loadFavorites = async () => {
      setLoading(true);
      const favoriteIds = JSON.parse(localStorage.getItem('rani_favorites') || '[]');
      setFavorites(favoriteIds);

      if (favoriteIds.length > 0) {
        // Fetch all favorite products
        const productPromises = favoriteIds.map(id => 
          fetch(`${API}/products/${id}`).then(res => res.ok ? res.json() : null)
        );
        const results = await Promise.all(productPromises);
        setProducts(results.filter(Boolean));
      } else {
        setProducts([]);
      }
      setLoading(false);
    };

    loadFavorites();
    
    // Listen for favorites updates
    const handleUpdate = () => loadFavorites();
    window.addEventListener('favoritesUpdated', handleUpdate);
    return () => window.removeEventListener('favoritesUpdated', handleUpdate);
  }, [API]);

  const clearAllFavorites = () => {
    localStorage.setItem('rani_favorites', '[]');
    setFavorites([]);
    setProducts([]);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A1625] flex items-center gap-3">
            <Heart className="h-8 w-8 text-red-500 fill-red-500" />
            {t.title}
          </h1>
          {products.length > 0 && (
            <button
              onClick={clearAllFavorites}
              className="text-sm text-red-500 hover:text-red-600 underline"
            >
              {t.removeAll}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          </div>
        ) : products.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6"
          >
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </motion.div>
        ) : (
          <div className="text-center py-20">
            <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-muted-foreground text-lg mb-6">{t.empty}</p>
            <Link
              to="/shop"
              className="inline-block bg-primary hover:bg-purple-600 text-white px-8 py-3 rounded-full font-medium transition-colors"
            >
              {t.browseProducts}
            </Link>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default FavoritesPage;
